import * as React from 'react';
import { FC } from 'react';

import styled from 'styled-components';

import { useDistanceWarning } from 'hooks/use-distance-warning';
import { StoreCardButtonOptions } from 'state/launchdarkly/variations';
import { convertCountryStringToIsoCountryCode2 } from 'utils/i18n';
import { fadeIn } from 'utils/style/animations';

import { useMessage } from './hooks/use-message';
import { useStoreActionOptions } from './hooks/use-store-action-options';
import { StoreActionsView } from './store-actions.view';
import { IActionOption, IStoreActionsProps } from './types';

const FadeIn = styled.div`
  animation: 0.2s ${fadeIn} forwards;
  opacity: 0;
`;

/**
 * StoreCardActions container component. This component is intended to be used
 * on the store locator. Data orchestration should happen at this level while
 * keeping the view layer more presentational.
 */
export const StoreActions: FC<IStoreActionsProps> = ({ restaurant }) => {
  const message = useMessage({ restaurant });
  const {
    isLocationConfirmationNeeded,
    ConfirmLocationModal,
    dismissDialog,
    openConfirmationModal,
    isLoading,
    isGeolocationEnabled,
  } = useDistanceWarning({ isMobileOrdering: true });

  /**
   * CREATE STORE ACTION OPTIONS
   */
  const actionOptions: IActionOption[] = useStoreActionOptions({
    restaurant,
  });

  const orderAction = actionOptions.find(
    storeAction => storeAction.id === StoreCardButtonOptions.ORDER
  );

  const getOrderOption = async () => {
    const hasCoordinates =
      typeof restaurant?.latitude === 'number' && typeof restaurant?.longitude === 'number';

    if (!hasCoordinates) {
      return orderAction?.onClick();
    }

    const restaurantCoordinates = {
      lat: restaurant.latitude,
      lng: restaurant.longitude,
    };

    const isFarAway = await isLocationConfirmationNeeded(restaurantCoordinates);

    return isFarAway ? openConfirmationModal() : orderAction?.onClick();
  };

  const modifiedActionOptions = actionOptions.map(action => {
    if (action.id === StoreCardButtonOptions.ORDER) {
      const newLoading = action.loading || isLoading;

      return {
        ...action,
        loading: newLoading,
        onClick: getOrderOption,
      };
    }
    return action;
  });

  const onConfirmDistanceWarning = () => {
    orderAction?.onClick();
    dismissDialog();
  };

  /**
   * RENDER VIEW
   */
  return (
    <>
      <FadeIn>
        <StoreActionsView options={modifiedActionOptions} message={message} />
      </FadeIn>
      {restaurant?.physicalAddress && (
        <ConfirmLocationModal
          streetName={restaurant.physicalAddress.address1 || ''}
          city={`${restaurant.physicalAddress.city}, ${convertCountryStringToIsoCountryCode2(
            restaurant.physicalAddress.country || ''
          )}`}
          onConfirm={onConfirmDistanceWarning}
          onDismiss={dismissDialog}
          isGeolocationEnabled={isGeolocationEnabled}
        />
      )}
    </>
  );
};
